<template>
  <div class="ai-chat-container" :class="{ 'show': isVisible }">
    <div class="ai-chat-header">
      <h5 class="mb-0">{{ $t('ai_assistant') }}</h5>
      <button class="btn-close" @click="$emit('close')"></button>
    </div>
    
    <div class="ai-chat-messages" ref="messagesContainer">
      <div v-for="(message, index) in messages" :key="index" 
           :class="['message', message.type]">
        <div class="message-content">
          <div v-if="message.type === 'ai'" class="ai-avatar">
            <AppIcon name="bi-robot" class="bi bi-robot" />
          </div>
          <div class="message-text">
            <div v-if="message.type === 'ai' && message.content.results" class="results-container">
              <div class="results-message">{{ message.content.message }}</div>
              <div v-if="message.content.results.length > 0" class="results-table">
                <table>
                  <thead>
                    <tr>
                      <th v-for="(value, key) in message.content.results[0]" :key="key" 
                          :class="getColumnClass(key)">
                        {{ key }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, rowIndex) in message.content.results" :key="rowIndex">
                      <td v-for="(value, key) in row" :key="key" 
                          :class="getColumnClass(key)">
                        {{ value }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="no-results">
                {{ $t('no_results_found') }}
              </div>
            </div>
            <div v-else>{{ message.content }}</div>
          </div>
        </div>
      </div>
      <div v-if="isLoading" class="message ai">
        <div class="message-content">
          <div class="ai-avatar">
            <AppIcon name="bi-robot" class="bi bi-robot" />
          </div>
          <div class="message-text">
            <div class="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="ai-chat-input">
      <textarea 
        v-model="userInput" 
        @keydown.enter.prevent="sendMessage"
        :placeholder="$t('ask_ai_question')"
        rows="1"
        ref="input"
      ></textarea>
      <button 
        class="btn btn-primary" 
        @click="sendMessage"
        :disabled="isLoading || !userInput.trim()"
      >
        <AppIcon name="bi-send" class="bi bi-send" />
      </button>
    </div>
  </div>
</template>

<script>
import helper from '../helper'

export default {
  name: 'AIChat',
  data() {
    return {
      messages: [],
      userInput: '',
      isLoading: false,
      isVisible: false,
      conversationContext: {}
    }
  },
  mounted() {
    this.isVisible = true
    this.$nextTick(() => {
      this.$refs.input.focus()
    })
    this.addMessage('ai', this.$t('ai_welcome_message'))
  },
  methods: {
    async sendMessage() {
      if (!this.userInput.trim() || this.isLoading) return

      const userMessage = this.userInput.trim()
      this.addMessage('user', userMessage)
      this.userInput = ''
      this.isLoading = true

      try {
        const response = await helper.customPost(`${process.env.VUE_APP_API}/businesses/ai-sql`, {
          query: userMessage,
          conversationContext: this.conversationContext
        })

        if (response.data) {
          // Update conversation context
          this.conversationContext = response.data.context || {}

          if (response.data.needsMoreInfo) {
            // If we need more information, just show the message
            this.addMessage('ai', this.formatAIOutput(userMessage, response.data))
          } else {
            // If we have all information, show results
            if (response.data.results !== undefined) {
              this.addMessage('ai',this.formatAIOutput(userMessage, response.data))
            }
          }
        }
      } catch (error) {
        this.addMessage('ai', this.$t('ai_error_message'))
        // Reset conversation context on error
        this.conversationContext = {}
      } finally {
        this.isLoading = false
        this.scrollToBottom()
      }
    },
    formatAIOutput(query, response) {
      const { message, results } = response;

      // Handle missing or invalid results
      if (!Array.isArray(results) || results.length === 0) {
        return {
          message: "No results found for your query.",
          results: []
        };
      }

      // Format the results for display
      const formattedResults = results.map(row => {
        const formattedRow = {};
        for (const [key, value] of Object.entries(row)) {
          // Format the key (column name)
          const formattedKey = key
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();

          // Format the value based on its type
          let formattedValue = value;
          if (value === null || value === undefined || value === 'null') {
            formattedValue = '-';
          } else if (typeof value === 'number') {
            formattedValue = new Intl.NumberFormat('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(value);
          } else if (typeof value === 'boolean') {
            formattedValue = value ? 'Yes' : 'No';
          } else if (value instanceof Date) {
            formattedValue = value.toLocaleString();
          }

          formattedRow[formattedKey] = formattedValue;
        }
        return formattedRow;
      });

      return {
        message: message,
        results: formattedResults
      };
    },
    addMessage(type, content, isSQL = false) {
      this.messages.push({ type, content, isSQL })
      this.scrollToBottom()
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        container.scrollTop = container.scrollHeight
      })
    },
    formatColumnHeader(key) {
      // Convert snake_case or camelCase to Title Case with spaces
      return key
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim()
    },
    getColumnClass(key) {
      const classes = []
      
      // Add specific classes based on column type
      if (key.toLowerCase().includes('date')) {
        classes.push('date-column')
      }
      if (key.toLowerCase().includes('amount') || key.toLowerCase().includes('price') || key.toLowerCase().includes('total')) {
        classes.push('number-column')
      }
      if (key.toLowerCase().includes('status')) {
        classes.push('status-column')
      }
      
      return classes.join(' ')
    }
  }
}
</script>

<style scoped>
.ai-chat-container {
  position: fixed;
  top: 0;
  right: -400px;
  width: 400px;
  height: 100vh;
  background: white;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  transition: right 0.3s ease;
  z-index: 1000;
}

.ai-chat-container.show {
  right: 0;
}

.ai-chat-header {
  padding: 1rem;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
}

.ai-chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.message {
  max-width: 85%;
}

.message.user {
  align-self: flex-end;
}

.message.ai {
  align-self: flex-start;
}

.message-content {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
}

.ai-avatar {
  width: 32px;
  height: 32px;
  background: #e9ecef;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.message-text {
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.9rem;
}

.message.user .message-text {
  background: #0d6efd;
  color: white;
}

.sql-code {
  background: #2d2d2d;
  color: #fff;
  padding: 0.75rem;
  border-radius: 0.25rem;
  font-family: monospace;
  white-space: pre-wrap;
  margin: 0;
}

.ai-chat-input {
  padding: 1rem;
  border-top: 1px solid #eee;
  display: flex;
  gap: 0.5rem;
  background: white;
}

.ai-chat-input textarea {
  flex: 1;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 0.5rem;
  resize: none;
  font-size: 0.9rem;
}

.ai-chat-input textarea:focus {
  outline: none;
  border-color: #0d6efd;
}

.typing-indicator {
  display: flex;
  gap: 0.25rem;
  padding: 0.5rem;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: #0d6efd;
  border-radius: 50%;
  animation: typing 1s infinite ease-in-out;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

.results-container {
  width: 100%;
  overflow-x: auto;
}

.results-message {
  margin-bottom: 0.75rem;
  font-weight: 500;
  color: #495057;
}

.results-table {
  width: 100%;
  overflow-x: auto;
  margin-top: 0.5rem;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.results-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.results-table th,
.results-table td {
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  text-align: left;
}

.results-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  white-space: nowrap;
}

.results-table tr:nth-child(even) {
  background-color: #f8f9fa;
}

.results-table tr:hover {
  background-color: #e9ecef;
}

/* Column type specific styles */
.number-column {
  text-align: right;
  font-family: monospace;
}

.date-column {
  white-space: nowrap;
}

.status-column {
  text-transform: capitalize;
}

/* Message styles */
.message.ai .message-text {
  white-space: pre-wrap;
  line-height: 1.5;
}

.message.ai .message-text strong {
  color: #0d6efd;
}

.message.ai .message-text ul {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.message.ai .message-text li {
  margin: 0.25rem 0;
}

.no-results {
  color: #6c757d;
  font-style: italic;
  padding: 0.5rem 0;
}
</style> 